import { Loader2, Settings2 } from "lucide-react";
import type { StatusReportTemplate } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { useStatusReportTemplatesStore } from "@/store/statusReportTemplatesStore";
import { TEMPLATE_DISPLAY } from "./templateDisplay";

interface StatusReportTemplateRowProps {
  template: StatusReportTemplate;
  onEdit: (template: StatusReportTemplate) => void;
}

/**
 * One template's row in the Report Settings panel: an enabled switch (saved
 * inline — enabling is what starts this template's scheduled sends), the name
 * and description, and the entry point to its settings sheet.
 */
export function StatusReportTemplateRow({
  template,
  onEdit,
}: StatusReportTemplateRowProps) {
  const loadingByKey = useStatusReportTemplatesStore(
    (state) => state.loadingByKey,
  );
  const setTemplateEnabled = useStatusReportTemplatesStore(
    (state) => state.setTemplateEnabled,
  );
  const isSaving = loadingByKey[template.templateKey]?.isLoadingSave ?? false;
  const display = TEMPLATE_DISPLAY[template.templateKey];

  return (
    <SettingsRow
      id={`report-template-${template.templateKey}`}
      label={display.title}
      description={display.description}
    >
      <div className="flex items-center gap-3">
        {isSaving && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        <Switch
          checked={template.enabled}
          disabled={isSaving}
          onCheckedChange={(enabled) =>
            setTemplateEnabled(template.templateKey, enabled)
          }
          aria-label={`${display.title} enabled`}
        />
        <Button variant="outline" size="sm" onClick={() => onEdit(template)}>
          <Settings2 className="mr-1.5 h-3.5 w-3.5" />
          Edit settings
        </Button>
      </div>
    </SettingsRow>
  );
}
